/**
 * ProductAutocomplete — busca productos EN EL SERVIDOR mientras se escribe.
 *
 * Reemplaza el patrón de descargar los primeros N productos y filtrar en el
 * navegador. Con ese patrón, en un catálogo de más de N productos los que
 * quedaban fuera no se podían buscar ni por SKU ni por nombre (ditdental tiene
 * ~1.075 y los formularios pedían 500, 2026-10-02). La búsqueda del servidor
 * además rankea: SKU exacto, después los que empiezan igual, y busca también en
 * la descripción y las compatibilidades.
 *
 * `useProductLookup` (hooks/useProductLookup.ts) complementa: resuelve los productos de las líneas ya
 * cargadas (para precio, stock, unidad) y recuerda los que se eligen, así las
 * pantallas siguen usando `products.find(...)` sin conocer el catálogo entero.
 */
import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { salesKeys, type ProductListItem, type ProductListResponse } from '../hooks/useSales'
import { useDebounced } from '@/hooks/useDebounced'

const DEBOUNCE_MS = 250
const RESULTADOS = 20

interface ProductAutocompleteProps {
  /** Qué muestra cada resultado como texto principal (el otro va de subtítulo). */
  field: 'sku' | 'name'
  value: string
  onChange: (value: string) => void
  onSelectProduct: (product: ProductListItem) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  id?: string
  'aria-label'?: string
  /** Por defecto solo activos; `all` incluye inactivos. */
  active?: 'true' | 'all'
  /** Acota a productos o servicios (los servicios no llevan stock). */
  type?: 'product' | 'service'
}

export function ProductAutocomplete({
  field, value, onChange, onSelectProduct, placeholder, className, disabled, id,
  'aria-label': ariaLabel, active = 'true', type,
}: ProductAutocompleteProps) {
  const [query, setQuery] = useState('')
  const [abierto, setAbierto] = useState(false)
  const termino = useDebounced(query.trim(), DEBOUNCE_MS)

  const params = { active, type, search: termino || undefined, page: 1, page_size: RESULTADOS }
  const { data, isFetching } = useQuery<ProductListResponse>({
    queryKey: salesKeys.products(params),
    queryFn: async () => (await apiClient.get('/sales/products/', { params })).data,
    enabled: abierto && !disabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
  const resultados = useMemo(() => data?.products ?? [], [data])

  const items: AutocompleteItem[] = useMemo(() => resultados.map(p => (
    field === 'sku'
      ? { id: p.id, label: p.sku, sublabel: p.name }
      : { id: p.id, label: p.name, sublabel: p.sku }
  )), [resultados, field])

  return (
    <Autocomplete
      items={items}
      value={value}
      onChange={onChange}
      onQueryChange={q => { setQuery(q); setAbierto(true) }}
      onSelect={item => {
        const prod = resultados.find(p => p.id === item.id)
        if (prod) onSelectProduct(prod)
      }}
      serverFiltered
      loading={isFetching || query.trim() !== termino}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      id={id}
      aria-label={ariaLabel}
      freeText
    />
  )
}
